import React, { useState, useEffect } from 'react';
import { AvatarModel } from '../../types/avatar';
import Modal from '../../Common/Components/Modal';
import { AvatarsService } from '../../services/avatarsService';
import { Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';

interface UploadAvatarDialogProps {
  onClose: () => void;
  onSave: (avatar: AvatarModel) => Promise<void>;
  show: boolean;
}

const UploadAvatarDialog: React.FC<UploadAvatarDialogProps> = ({
  onClose,
  onSave,
  show
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleSave = async () => {
    if (!selectedFile) {
      toast.error('Please select an avatar image');
      return;
    }

    setIsLoading(true);
    try {
      const photoUrl = await AvatarsService.uploadAvatarImage(selectedFile);

      const avatar: AvatarModel = {
        id: '', // Will be generated by Firestore
        photoUrl,
      };

      await onSave(avatar);
      onClose();

      setSelectedFile(null);
    } catch (error) {
      console.error('Error saving avatar:', error);
      toast.error('Failed to upload avatar. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setSelectedFile(null);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} id="uploadAvatarModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Upload New Avatar</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Avatar Image *</label>
            <div className="relative border-2 border-dashed border-slate-300 dark:border-zink-500 rounded-lg p-4 cursor-pointer" onClick={() => document.getElementById('avatar-image-input')?.click()}>
              <div className="text-center">
                {selectedFile ? (
                  <>
                    <div className="size-16 bg-slate-100 rounded-full dark:bg-zink-600 mx-auto mb-2 flex items-center justify-center">
                      <img
                        src={previewUrl || ''}
                        alt="Selected avatar"
                        className="h-16 w-16 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) nextElement.style.display = 'flex';
                        }}
                      />
                      <div className="size-16 bg-gray-100 rounded-full dark:bg-gray-500/20 items-center justify-center hidden">
                        <span className="text-gray-500 text-2xl">👤</span>
                      </div>
                    </div>
                    <p className="text-sm font-medium mb-1">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500 dark:text-zink-400 mb-2">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile();
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-red-100 text-red-600 rounded-md hover:bg-red-200 dark:bg-red-500/20 dark:hover:bg-red-500/30"
                    >
                      <X className="size-3" />
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <Upload className="size-8 text-slate-400 dark:text-zink-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 dark:text-zink-300 mb-2">
                      Click to upload avatar image
                    </p>
                    <p className="text-xs text-slate-500 dark:text-zink-400">
                      PNG, JPG up to 5MB
                    </p>
                  </>
                )}
              </div>
              <input
                id="avatar-image-input"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
          disabled={isLoading}
        >
          Close
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Upload
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadAvatarDialog;